import React, { useState, useContext, useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
// Lazy load rsuite Uploader only when Pictures mounts
const LazyUploader = React.lazy(() => Promise.all([
  import(/* webpackChunkName: "rsuite-uploader-css" */ 'rsuite/Uploader/styles/index.css'),
  import(/* webpackChunkName: "rsuite-uploader" */ 'rsuite/Uploader')
]).then(([, m]) => ({ default: (m as any).default || m })));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Uploader: React.FC<any> = (props) => (
  <React.Suspense fallback={<div className="card h-100 d-flex align-items-center justify-content-center"><div className="text-muted small py-2">Loading…</div></div>}>
    <LazyUploader {...props} />
  </React.Suspense>
);
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { apiClient } from '../api/client';
import { AppContext } from '../window';
import { NotificationContext } from '../context/NotificationContext';
import { createDefaultPicture, Picture } from '../models/Picture';
import { useTranslation } from '../i18n';
const ImagePreviewModal = React.lazy(() => import(/* webpackChunkName: "image-preview-modal" */ './ImagePreviewModal'));

interface PicturesProps {
  onPictureUploaded?: () => void;
}

const Pictures: React.FC<PicturesProps> = ({ onPictureUploaded }) => {
  // styles now loaded via lazy import above
  const appContext = useContext(AppContext);
  const { user, setUser } = appContext;
  const [loading, setLoading]  = useState(false);
  const { addNotification } = useContext(NotificationContext);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploaderKey, setUploaderKey] = useState(0);
  const { t } = useTranslation();

  const handlePictureDelete = async (id: number) => {
    if (!user) return;
    setLoading(true);
    try {
  const result = await apiClient.postJson<any>(`/user/picture/delete?id=${id}&json=1`);
  if (!result.ok) throw result.error;
      const newPictures = { ...user.pictures };
      delete newPictures[id];
      let newUser = { ...user, pictures: newPictures };
      
      // If deleting the display picture and there are remaining pictures, set one as the new display picture
      if (user.displayPicture && user.displayPicture.id === id) {
        const remainingPictures = Object.values(newPictures).filter((pic: any) => pic.private === 0);
        if (remainingPictures.length > 0) {
          // Set the first remaining public picture as the new display picture
          const newDisplayPicture = remainingPictures[0];
          if (newDisplayPicture.id) {
            await handleSetProfilePicture(newDisplayPicture.id, true, newPictures);
          }
          // Show deletion notification after setting new profile picture
          addNotification({ message: t('pictures.picture_deleted'), level: 'info' });
          setLoading(false);
          return;
        } else {
          // No remaining pictures, use default
          newUser.displayPicture = createDefaultPicture(user.id, user.gender);
        }
      }
      
      setUser(newUser);
      addNotification({ message: t('pictures.picture_deleted'), level: 'info' });
      // Force uploader to remount to clear any cached state
      setUploaderKey(prev => prev + 1);
    } catch (err) {
      addNotification({ message: t('pictures.delete_failed'), level: 'danger' });
    }
    setLoading(false);
  };

  const handleSetProfilePicture = async (id: number, silent: boolean = false, pictures?: Record<string, Picture>) => {
    if (!user) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', id.toString());
      
  const result = await apiClient.postForm<any>('/user/api/profile-picture', formData);
  if (!result.ok) throw result.error;
  const res = result.data || {};
      
      if (res.success) {
        // Use provided pictures or current user pictures
        const currentPictures = pictures || user.pictures;
        // Update the pictures to reflect the new main picture
        const updatedPictures = Object.keys(currentPictures).reduce((acc, key) => {
          const pic = currentPictures[key];
          acc[key] = {
            ...pic,
            main: pic.id === id ? 1 : 0
          };
          return acc;
        }, {} as typeof user.pictures);
        
        const newDisplayPicture = updatedPictures[id];
        setUser({ ...user, pictures: updatedPictures, displayPicture: newDisplayPicture });
        if (!silent) {
          addNotification({ message: res.message || t('pictures.profile_picture_updated'), level: 'info' });
        }
      } else if (res.error) {
        addNotification({ message: res.error, level: 'danger' });
      }
    } catch (err) {
      console.error('Failed to set profile picture:', err);
      addNotification({ message: t('pictures.failed_set_profile_picture'), level: 'danger' });
    }
    setLoading(false);
  };

  const handleUpload = async (fileList: any[]) => {
    if (!fileList || fileList.length === 0 || !user) return;
    const fileObj = fileList[fileList.length - 1];
    const file = fileObj.blobFile;
    if (!(file instanceof Blob)) {
      addNotification({ message: t('pictures.upload_failed_not_found'), level: 'danger' });
      return;
    }
    
    // Show preview modal instead of uploading directly
    setPreviewFile(file as File);
    setShowPreview(true);
  };

  const handleConfirmUpload = async (croppedFile: File) => {
    if (!user) return;
    setShowPreview(false);
    setLoading(true);
    
    const formData = new FormData();
    formData.append('UserPicture[_upload]', croppedFile);
    formData.append('UserPicture[private]', '0');
    
    try {
  const result = await apiClient.postForm<any>('/user/picture/send', formData);
  if (!result.ok) throw result.error;
  const res = result.data || {};
      if (!res.error) {
        const newPictures = { ...user.pictures, [res.id]: res };
        if(!user.displayPicture || !user.displayPicture.id || user.displayPicture.id === -1) {
          user.displayPicture = res as Picture;
        }

        setUser({ ...user, pictures: newPictures });
        addNotification({ message: t('pictures.picture_uploaded'), level: 'info' });
        
        // Call the callback if provided
        if (onPictureUploaded) {
          onPictureUploaded();
        }
      } else {
        addNotification({ message: res.error, level: 'danger' });
      }
    } catch (err) {
      addNotification({ message: t('pictures.upload_failed'), level: 'danger' });
    }
    setLoading(false);
    setPreviewFile(null);
    // Also increment key after upload to ensure fresh state for next upload
    setUploaderKey(prev => prev + 1);
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewFile(null);
    // Reset uploader on cancel to ensure clean state
    setUploaderKey(prev => prev + 1);
  };

  return (
    <>
      <Row className="pictures-list">
        <Col xs={12} md={3} className="picture-item mb-3">
          <Uploader
            key={uploaderKey}
            className="card h-100 d-flex flex-column justify-content-between"
            draggable
            accept="image/*"
            disabled={loading}
            action="#"
            fileList={[]}
            autoUpload={false}
            onChange={handleUpload}
          >
            <div className="thumbnail-card d-flex align-items-center justify-content-center h-100 py-3">
              <div style={{ textAlign: 'center', color: '#888', cursor: 'pointer' }}>
                <CloudUploadIcon style={{ fontSize: 40, marginBottom: 8 }} />
                <div style={{ fontSize: 13 }}>{t('pictures.drag_drop_upload')}</div>
              </div>
            </div>
          </Uploader>
        </Col>
        {/* Render user pictures as thumbnails */}
        {user && Object.values(user.pictures)
          .filter((pic: any) => pic.private === 0)
          .map((pic: any) => (
            <Col xs={12} md={3} key={pic.id} className="picture-item mb-3">
              <div className="card">
                <div className="has-thumbnail mb-2">
                  <div className="thumbnail-card">
                    <picture className="thumbnail">
                      <img src={pic.thumb || pic.large} alt="Profile" className="img-fluid" loading="lazy" decoding="async" />
                    </picture>
                  </div>
                </div>
                <div className="card-body p-2 d-flex gap-2">
                  {!pic.main && user.displayPicture?.id !== pic.id && (
                    <Button
                      onClick={() => handleSetProfilePicture(pic.id)}
                      variant="outline-primary"
                      size="sm"
                      disabled={loading}
                      className="w-100"
                    >
                      {t('pictures.set_as_avatar')}
                    </Button>
                  )}
                  <Button onClick={() => handlePictureDelete(pic.id)} variant="danger" size="sm" disabled={loading} className="w-100">{t('pictures.delete')}</Button>
                </div>
              </div>
            </Col>
          ))}
      </Row>
      
      {showPreview && (
        <React.Suspense fallback={null}>
          <ImagePreviewModal
            show={showPreview}
            file={previewFile}
            onConfirm={handleConfirmUpload}
            onCancel={handleCancelPreview}
          />
        </React.Suspense>
      )}
    </>
  );
};

export default Pictures;
